/**
 * Cron Expression Parser & Schedule Generator
 * High-performance 100% client-side parser, natural language translator,
 * and upcoming execution generator supporting 5-part and 6-part cron syntax.
 */

(function () {
  'use strict';

  // Month and Day name lookups
  const MONTH_NAMES = {
    JAN: 1, FEB: 2, MAR: 3, APR: 4, MAY: 5, JUN: 6,
    JUL: 7, AUG: 8, SEP: 9, OCT: 10, NOV: 11, DEC: 12
  };
  const MONTH_FULL = [
    '', 'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const DAY_NAMES = {
    SUN: 0, MON: 1, TUE: 2, WED: 3, THU: 4, FRI: 5, SAT: 6
  };
  const DAY_FULL = [
    'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
  ];

  // Presets definition
  const PRESETS = [
    { label: 'Every minute', expr: '* * * * *', seconds: false, desc: 'Runs every minute at second 0' },
    { label: 'Every 5 mins', expr: '*/5 * * * *', seconds: false, desc: 'Runs every 5 minutes' },
    { label: 'Every 15 mins', expr: '*/15 * * * *', seconds: false, desc: 'Runs every 15 minutes' },
    { label: 'Every hour', expr: '0 * * * *', seconds: false, desc: 'Runs at minute 0 of every hour' },
    { label: 'Every 2 hours', expr: '0 */2 * * *', seconds: false, desc: 'Runs every 2 hours at minute 0' },
    { label: 'Daily midnight', expr: '0 0 * * *', seconds: false, desc: 'Runs daily at 12:00 AM (00:00)' },
    { label: 'Daily at 9 AM', expr: '0 9 * * *', seconds: false, desc: 'Runs daily at 09:00 AM' },
    { label: 'Weekdays at 9 AM', expr: '0 9 * * 1-5', seconds: false, desc: 'Runs Monday to Friday at 09:00 AM' },
    { label: 'Twice daily', expr: '0 9,18 * * *', seconds: false, desc: 'Runs daily at 9:00 AM and 6:00 PM' },
    { label: 'Every Sunday', expr: '0 0 * * 0', seconds: false, desc: 'Runs every Sunday at midnight' },
    { label: '1st of month', expr: '0 0 1 * *', seconds: false, desc: 'Runs on day 1 of every month at midnight' },
    { label: 'Every 10 secs', expr: '*/10 * * * * *', seconds: true, desc: 'Runs every 10 seconds (6-part cron)' },
    { label: 'Every 30 secs', expr: '*/30 * * * * *', seconds: true, desc: 'Runs every 30 seconds (6-part cron)' }
  ];

  // State
  let state = {
    rawExpr: '* * * * *',
    includeSeconds: false,
    dateCount: 10,
    timezoneMode: 'local', // 'local' or 'utc'
    lastValidParsed: null
  };

  /**
   * Parse a single cron sub-field into an array of matched numbers
   */
  function parseField(fieldStr, min, max, nameMap = null) {
    let str = fieldStr.trim().toUpperCase();

    // Replace names if applicable
    if (nameMap) {
      for (const [name, val] of Object.entries(nameMap)) {
        str = str.replace(new RegExp('\\b' + name + '\\b', 'g'), val);
      }
    }

    if (str === '*' || str === '?') {
      const all = [];
      for (let i = min; i <= max; i++) all.push(i);
      return { values: all, isWildcard: true, isStep: false, stepVal: 1 };
    }

    const values = new Set();
    let isStep = false;
    let stepVal = 1;

    const parts = str.split(',');
    for (const part of parts) {
      if (!part) throw new Error(`Empty segment in '${fieldStr}'`);

      if (part.includes('/')) {
        const [rangePart, stepStr] = part.split('/');
        const step = parseInt(stepStr, 10);
        if (isNaN(step) || step <= 0) {
          throw new Error(`Invalid step '${stepStr}' in '${fieldStr}'`);
        }
        isStep = true;
        stepVal = step;

        let start = min;
        let end = max;
        if (rangePart !== '*' && rangePart !== '?') {
          if (rangePart.includes('-')) {
            const [sStr, eStr] = rangePart.split('-');
            start = parseInt(sStr, 10);
            end = parseInt(eStr, 10);
          } else {
            start = parseInt(rangePart, 10);
          }
        }
        if (isNaN(start) || isNaN(end) || start < min || end > max || start > end) {
          throw new Error(`Invalid range '${rangePart}' in '${fieldStr}'. Allowed: ${min}-${max}`);
        }
        for (let i = start; i <= end; i += step) {
          values.add(i);
        }
      } else if (part.includes('-')) {
        const [sStr, eStr] = part.split('-');
        const start = parseInt(sStr, 10);
        const end = parseInt(eStr, 10);
        if (isNaN(start) || isNaN(end) || start < min || end > max || start > end) {
          throw new Error(`Invalid range '${part}' in '${fieldStr}'. Allowed: ${min}-${max}`);
        }
        for (let i = start; i <= end; i++) {
          values.add(i);
        }
      } else {
        // Special Quartz character handling L, W, #
        let cleanPart = part;
        if (cleanPart.endsWith('L')) {
          cleanPart = cleanPart.replace('L', '') || min;
        }
        if (cleanPart.endsWith('W')) {
          cleanPart = cleanPart.replace('W', '');
        }
        if (cleanPart.includes('#')) {
          cleanPart = cleanPart.split('#')[0];
        }

        let num = parseInt(cleanPart, 10);
        if (nameMap === DAY_NAMES && num === 7) num = 0; // standard 7=Sunday alias

        if (isNaN(num) || num < min || num > max) {
          throw new Error(`Invalid value '${part}' in '${fieldStr}'. Allowed: ${min}-${max}`);
        }
        values.add(num);
      }
    }

    const sorted = Array.from(values).sort((a, b) => a - b);
    if (sorted.length === 0) {
      throw new Error(`Field '${fieldStr}' has no valid values in range ${min}-${max}`);
    }
    return { values: sorted, isWildcard: false, isStep, stepVal };
  }

  /**
   * Validate and parse a complete cron expression string
   */
  function parseCronExpression(expr, includeSeconds) {
    const tokens = expr.trim().split(/\s+/).filter(Boolean);
    const expectedCount = includeSeconds ? 6 : 5;

    if (tokens.length !== expectedCount) {
      throw new Error(
        `Expected ${expectedCount} fields (${includeSeconds ? 'second, minute, hour, day-of-month, month, day-of-week' : 'minute, hour, day-of-month, month, day-of-week'}), but found ${tokens.length}.`
      );
    }

    let secParsed = { values: [0], isWildcard: false, raw: '0' };
    let offset = 0;

    if (includeSeconds) {
      secParsed = { ...parseField(tokens[0], 0, 59), raw: tokens[0] };
      offset = 1;
    }

    const minParsed = { ...parseField(tokens[offset + 0], 0, 59), raw: tokens[offset + 0] };
    const hourParsed = { ...parseField(tokens[offset + 1], 0, 23), raw: tokens[offset + 1] };
    const domParsed = { ...parseField(tokens[offset + 2], 1, 31), raw: tokens[offset + 2] };
    const monParsed = { ...parseField(tokens[offset + 3], 1, 12, MONTH_NAMES), raw: tokens[offset + 3] };
    const dowParsed = { ...parseField(tokens[offset + 4], 0, 6, DAY_NAMES), raw: tokens[offset + 4] };

    return {
      tokens,
      includeSeconds,
      sec: secParsed,
      min: minParsed,
      hour: hourParsed,
      dom: domParsed,
      mon: monParsed,
      dow: dowParsed
    };
  }

  /**
   * Convert parsed fields into human-readable plain English sentence
   */
  function toHumanReadable(parsed) {
    const { sec, min, hour, dom, mon, dow, includeSeconds } = parsed;

    const pad = n => String(n).padStart(2, '0');
    const formatTime = (h, m, s = null) => {
      const ampm = h >= 12 ? 'PM' : 'AM';
      const h12 = h % 12 === 0 ? 12 : h % 12;
      if (s !== null && includeSeconds) {
        return `${pad(h12)}:${pad(m)}:${pad(s)} ${ampm}`;
      }
      return `${pad(h12)}:${pad(m)} ${ampm}`;
    };

    let parts = [];

    // 1. Seconds (if 6-part)
    if (includeSeconds) {
      if (sec.isWildcard) {
        parts.push('Every second');
      } else if (sec.raw.startsWith('*/')) {
        parts.push(`Every ${sec.stepVal} seconds`);
      } else if (sec.values.length === 1 && sec.values[0] !== 0) {
        parts.push(`At second ${sec.values[0]}`);
      }
    }

    // 2. Minute & Hour combination
    if (min.isWildcard && hour.isWildcard) {
      if (!includeSeconds || (sec.values.length === 1 && sec.values[0] === 0)) {
        parts.push('Every minute');
      }
    } else if (min.raw.startsWith('*/') && hour.isWildcard) {
      parts.push(`Every ${min.stepVal} minutes`);
    } else if (min.isWildcard && !hour.isWildcard) {
      if (hour.raw.startsWith('*/')) {
        parts.push(`Every minute, every ${hour.stepVal} hours`);
      } else {
        const hourList = hour.values.map(h => formatTime(h, 0).replace(':00 ', ' ')).join(', ');
        parts.push(`Every minute during hour ${hourList}`);
      }
    } else if (!min.isWildcard && hour.isWildcard) {
      if (hour.raw.startsWith('*/')) {
        const minStr = min.values.map(m => `:${pad(m)}`).join(', ');
        parts.push(`At minute ${minStr} past every ${hour.stepVal} hours`);
      } else {
        const minStr = min.values.map(m => pad(m)).join(', ');
        parts.push(`At minute ${minStr} of every hour`);
      }
    } else if (!min.isWildcard && !hour.isWildcard) {
      if (hour.values.length === 1 && min.values.length === 1) {
        const s = (includeSeconds && sec.values.length === 1) ? sec.values[0] : null;
        parts.push(`At ${formatTime(hour.values[0], min.values[0], s)}`);
      } else if (hour.values.length > 1 && min.values.length === 1) {
        const times = hour.values.map(h => formatTime(h, min.values[0])).join(' and ');
        parts.push(`At ${times}`);
      } else {
        const hourList = hour.values.map(h => `${h}:00`).join(', ');
        const minList = min.values.join(', ');
        parts.push(`At minute ${minList} past hours ${hourList}`);
      }
    }

    // 3. Day of Month
    if (!dom.isWildcard) {
      if (dom.raw.startsWith('*/')) {
        parts.push(`every ${dom.stepVal} days`);
      } else if (dom.raw.includes('-')) {
        parts.push(`between day ${dom.raw} of the month`);
      } else if (dom.values.length === 1) {
        parts.push(`on day ${dom.values[0]} of the month`);
      } else {
        parts.push(`on days ${dom.values.join(', ')} of the month`);
      }
    }

    // 4. Month
    if (!mon.isWildcard) {
      if (mon.raw.startsWith('*/')) {
        parts.push(`every ${mon.stepVal} months`);
      } else if (mon.values.length === 1) {
        parts.push(`only in ${MONTH_FULL[mon.values[0]]}`);
      } else {
        const monthNames = mon.values.map(m => MONTH_FULL[m]).join(', ');
        parts.push(`in ${monthNames}`);
      }
    }

    // 5. Day of Week
    if (!dow.isWildcard) {
      if (dow.raw === '1-5' || (dow.values.length === 5 && dow.values.join(',') === '1,2,3,4,5')) {
        parts.push('Monday through Friday');
      } else if (dow.raw === '0,6' || dow.raw === '6,0' || (dow.values.length === 2 && dow.values.includes(0) && dow.values.includes(6))) {
        parts.push('on weekends (Saturday & Sunday)');
      } else if (dow.values.length === 1) {
        parts.push(`every ${DAY_FULL[dow.values[0]]}`);
      } else {
        const dayNames = dow.values.map(d => DAY_FULL[d]).join(', ');
        parts.push(`on ${dayNames}`);
      }
    }

    if (parts.length === 0) {
      return 'Every minute';
    }

    let sentence = parts.join(', ');
    sentence = sentence.charAt(0).toUpperCase() + sentence.slice(1);
    return sentence;
  }

  /**
   * Calculate next N occurrences of a parsed cron expression
   */
  function getNextRunDates(parsed, count = 10, startDate = new Date()) {
    const dates = [];
    const { sec, min, hour, dom, mon, dow, includeSeconds } = parsed;

    let current = new Date(startDate.getTime());
    current.setMilliseconds(0);
    current.setSeconds(current.getSeconds() + 1);

    if (!includeSeconds) {
      current.setSeconds(0);
      if (current.getTime() <= startDate.getTime()) {
        current.setMinutes(current.getMinutes() + 1);
      }
    }

    const secSet = new Set(sec.values);
    const minSet = new Set(min.values);
    const hourSet = new Set(hour.values);
    const domSet = new Set(dom.values);
    const monSet = new Set(mon.values);
    const dowSet = new Set(dow.values);

    const domWildcard = dom.isWildcard;
    const dowWildcard = dow.isWildcard;

    const maxIterations = 500000;
    let iterations = 0;

    while (dates.length < count && iterations < maxIterations) {
      iterations++;

      // 1. Check Month
      const currentMonth = current.getMonth() + 1;
      if (!monSet.has(currentMonth)) {
        current.setMonth(current.getMonth() + 1, 1);
        current.setHours(0, 0, 0, 0);
        continue;
      }

      // 2. Check Day of Month and Day of Week
      const currentDom = current.getDate();
      const currentDow = current.getDay();

      let dayMatches = false;
      if (domWildcard && dowWildcard) {
        dayMatches = true;
      } else if (!domWildcard && !dowWildcard) {
        dayMatches = domSet.has(currentDom) || dowSet.has(currentDow);
      } else if (!domWildcard) {
        dayMatches = domSet.has(currentDom);
      } else {
        dayMatches = dowSet.has(currentDow);
      }

      if (!dayMatches) {
        current.setDate(current.getDate() + 1);
        current.setHours(0, 0, 0, 0);
        continue;
      }

      // 3. Check Hour
      const currentHour = current.getHours();
      if (!hourSet.has(currentHour)) {
        current.setHours(current.getHours() + 1, 0, 0, 0);
        continue;
      }

      // 4. Check Minute
      const currentMin = current.getMinutes();
      if (!minSet.has(currentMin)) {
        current.setMinutes(current.getMinutes() + 1, 0, 0);
        continue;
      }

      // 5. Check Second (if 6-part cron)
      if (includeSeconds) {
        const currentSec = current.getSeconds();
        if (!secSet.has(currentSec)) {
          current.setSeconds(current.getSeconds() + 1);
          continue;
        }
      }

      // Found a match!
      dates.push(new Date(current.getTime()));

      if (includeSeconds) {
        current.setSeconds(current.getSeconds() + 1);
      } else {
        current.setMinutes(current.getMinutes() + 1);
      }
    }

    return dates;
  }

  function formatRelativeTime(targetDate, baseDate = new Date()) {
    const diffMs = targetDate.getTime() - baseDate.getTime();
    if (diffMs <= 0) return 'Just now';

    const diffSec = Math.round(diffMs / 1000);
    const diffMin = Math.round(diffSec / 60);
    const diffHour = Math.round(diffMin / 60);
    const diffDay = Math.round(diffHour / 24);

    if (diffSec < 60) return `in ${diffSec}s`;
    if (diffMin < 60) return `in ${diffMin}m`;
    if (diffHour < 24) return `in ${diffHour}h ${diffMin % 60}m`;
    if (diffDay === 1) return 'tomorrow';
    if (diffDay < 30) return `in ${diffDay} days`;
    return `in ${Math.round(diffDay / 30)} months`;
  }

  function formatDateTime(d, timezoneMode = 'local') {
    if (timezoneMode === 'utc') {
      return d.toUTCString().replace('GMT', 'UTC');
    }
    return d.toLocaleString(undefined, {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  }

  // --- UI CONTROLLER ---

  const DOM = {};

  function cacheDOMElements() {
    DOM.cronInput = document.getElementById('cronInput');
    DOM.secondsToggle = document.getElementById('secondsToggle');
    DOM.errorBanner = document.getElementById('errorBanner');
    DOM.errorMessage = document.getElementById('errorMessage');
    DOM.validSection = document.getElementById('validSection');
    DOM.humanReadable = document.getElementById('humanReadable');
    DOM.copyExplanationBtn = document.getElementById('copyExplanationBtn');
    DOM.copyCronBtn = document.getElementById('copyCronBtn');
    DOM.shareBtn = document.getElementById('shareBtn');
    DOM.tokenRuler = document.getElementById('tokenRuler');
    DOM.datesTableBody = document.getElementById('datesTableBody');
    DOM.datesCountSelect = document.getElementById('datesCountSelect');
    DOM.tzLocalBtn = document.getElementById('tzLocalBtn');
    DOM.tzUtcBtn = document.getElementById('tzUtcBtn');
    DOM.copyDatesBtn = document.getElementById('copyDatesBtn');
    DOM.presetsContainer = document.getElementById('presetsContainer');
    DOM.nextRunsCountBadge = document.getElementById('nextRunsCountBadge');
  }

  function renderPresets() {
    if (!DOM.presetsContainer) return;
    DOM.presetsContainer.innerHTML = PRESETS.map(preset => `
      <button type="button" class="cron-preset-chip ${preset.seconds ? 'chip-seconds' : ''}" 
        data-expr="${preset.expr}" 
        data-seconds="${preset.seconds}"
        title="${preset.desc}">
        <span class="chip-title">${preset.label}</span>
        <code class="chip-code">${preset.expr}</code>
      </button>
    `).join('');

    DOM.presetsContainer.querySelectorAll('.cron-preset-chip').forEach(btn => {
      btn.addEventListener('click', () => {
        const expr = btn.dataset.expr;
        const needsSeconds = btn.dataset.seconds === 'true';
        if (state.includeSeconds !== needsSeconds) {
          state.includeSeconds = needsSeconds;
          if (DOM.secondsToggle) DOM.secondsToggle.checked = needsSeconds;
        }
        DOM.cronInput.value = expr;
        handleInputChange();
        window.Analytics?.track('preset_select', { tool_name: 'cron_parser', preset_name: expr });
      });
    });
  }

  function renderTokenRuler(parsed) {
    if (!DOM.tokenRuler) return;

    const fields = [];
    if (parsed.includeSeconds) {
      fields.push({
        index: 0,
        name: 'Second',
        value: parsed.sec.raw,
        range: '0–59',
        example: '*, */10, 0,30'
      });
    }

    fields.push(
      {
        index: parsed.includeSeconds ? 1 : 0,
        name: 'Minute',
        value: parsed.min.raw,
        range: '0–59',
        example: '*, */5, 0,15,30,45'
      },
      {
        index: parsed.includeSeconds ? 2 : 1,
        name: 'Hour',
        value: parsed.hour.raw,
        range: '0–23',
        example: '*, */2, 9-17, 0'
      },
      {
        index: parsed.includeSeconds ? 3 : 2,
        name: 'Day of Month',
        value: parsed.dom.raw,
        range: '1–31',
        example: '*, 1, 15, L, ?'
      },
      {
        index: parsed.includeSeconds ? 4 : 3,
        name: 'Month',
        value: parsed.mon.raw,
        range: '1–12 or JAN–DEC',
        example: '*, */3, 1-6, JAN'
      },
      {
        index: parsed.includeSeconds ? 5 : 4,
        name: 'Day of Week',
        value: parsed.dow.raw,
        range: '0–6 or SUN–SAT',
        example: '*, 1-5, MON-FRI, 0'
      }
    );

    DOM.tokenRuler.innerHTML = fields.map((f, i) => `
      <div class="cron-field-card" data-token-idx="${f.index}">
        <div class="field-top">
          <span class="field-index">#${i + 1}</span>
          <span class="field-name">${f.name}</span>
        </div>
        <div class="field-val" title="Click to highlight in expression"><code>${escapeHtml(f.value)}</code></div>
        <div class="field-range">Allowed: ${f.range}</div>
      </div>
    `).join('');

    DOM.tokenRuler.querySelectorAll('.cron-field-card').forEach(card => {
      card.addEventListener('click', () => {
        const tokenIdx = parseInt(card.dataset.tokenIdx, 10);
        highlightTokenInInput(tokenIdx);
      });
    });
  }

  function highlightTokenInInput(tokenIdx) {
    if (!DOM.cronInput) return;
    const str = DOM.cronInput.value;
    const tokens = str.trim().split(/\s+/);
    if (tokenIdx >= tokens.length) return;

    let start = 0;
    let end = 0;
    let count = 0;
    let inToken = false;

    for (let i = 0; i < str.length; i++) {
      const isWhitespace = /\s/.test(str[i]);
      if (!isWhitespace && !inToken) {
        inToken = true;
        if (count === tokenIdx) start = i;
      } else if (isWhitespace && inToken) {
        inToken = false;
        if (count === tokenIdx) {
          end = i;
          break;
        }
        count++;
      }
    }
    if (inToken && count === tokenIdx) {
      end = str.length;
    }

    DOM.cronInput.focus();
    DOM.cronInput.setSelectionRange(start, end);
  }

  function renderUpcomingDates(parsed) {
    if (!DOM.datesTableBody) return;
    const count = parseInt(DOM.datesCountSelect ? DOM.datesCountSelect.value : state.dateCount, 10) || 10;
    state.dateCount = count;

    if (DOM.nextRunsCountBadge) {
      DOM.nextRunsCountBadge.textContent = `${count} Upcoming Runs`;
    }

    const now = new Date();
    const dates = getNextRunDates(parsed, count, now);

    if (dates.length === 0) {
      DOM.datesTableBody.innerHTML = `
        <tr>
          <td colspan="4" class="text-center py-4 text-muted">No upcoming execution dates found within search window.</td>
        </tr>
      `;
      return;
    }

    DOM.datesTableBody.innerHTML = dates.map((d, i) => {
      const relative = formatRelativeTime(d, now);
      const localStr = formatDateTime(d, 'local');
      const utcStr = d.toISOString().replace('.000Z', 'Z').replace('T', ' ');

      return `
        <tr class="cron-date-row">
          <td class="col-num">
            <span class="run-badge">#${i + 1}</span>
          </td>
          <td class="col-time">
            <div class="time-main">${state.timezoneMode === 'utc' ? utcStr : localStr}</div>
            <div class="time-sub">${state.timezoneMode === 'utc' ? localStr + ' (Local)' : utcStr + ' (UTC)'}</div>
          </td>
          <td class="col-relative">
            <span class="relative-pill ${i === 0 ? 'pill-next' : ''}">
              ${i === 0 ? '⚡ ' : ''}${relative}
            </span>
          </td>
          <td class="col-action">
            <button type="button" class="btn-copy-single" data-date="${d.toISOString()}" title="Copy ISO timestamp">
              📋
            </button>
          </td>
        </tr>
      `;
    }).join('');

    DOM.datesTableBody.querySelectorAll('.btn-copy-single').forEach(btn => {
      btn.addEventListener('click', () => {
        navigator.clipboard.writeText(btn.dataset.date).then(() => {
          showToast('Copied ISO date to clipboard');
        });
      });
    });
  }

  function handleInputChange() {
    const raw = DOM.cronInput.value;
    state.rawExpr = raw;
    state.includeSeconds = DOM.secondsToggle.checked;

    try {
      const parsed = parseCronExpression(raw, state.includeSeconds);
      state.lastValidParsed = parsed;

      DOM.errorBanner.hidden = true;
      DOM.validSection.hidden = false;

      const english = toHumanReadable(parsed);
      DOM.humanReadable.textContent = english;

      renderTokenRuler(parsed);
      renderUpcomingDates(parsed);

      updateUrlHash(raw, state.includeSeconds);

    } catch (err) {
      DOM.errorBanner.hidden = false;
      DOM.errorMessage.textContent = err.message || 'Invalid cron expression format.';
      DOM.validSection.hidden = true;
    }
  }

  function updateUrlHash(expr, seconds) {
    try {
      const sanitized = expr.trim().replace(/\s+/g, '_');
      const hash = (seconds ? 'sec_' : '') + sanitized;
      window.history.replaceState(null, '', '#' + hash);
    } catch (e) {}
  }

  function loadFromUrlHash() {
    try {
      let hash = window.location.hash.slice(1);
      if (!hash) return false;

      let isSec = false;
      if (hash.startsWith('sec_')) {
        isSec = true;
        hash = hash.replace('sec_', '');
      }
      const expr = hash.replace(/_/g, ' ').trim();
      if (expr) {
        state.includeSeconds = isSec;
        DOM.secondsToggle.checked = isSec;
        DOM.cronInput.value = expr;
        return true;
      }
    } catch (e) {}
    return false;
  }

  function showToast(msg) {
    let toast = document.getElementById('cronToast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'cronToast';
      toast.className = 'cron-toast';
      document.body.appendChild(toast);
    }
    toast.textContent = msg;
    toast.classList.add('show');
    clearTimeout(toast._timer);
    toast._timer = setTimeout(() => {
      toast.classList.remove('show');
    }, 2400);
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function setupEventListeners() {
    DOM.cronInput.addEventListener('input', handleInputChange);

    DOM.secondsToggle.addEventListener('change', () => {
      const checked = DOM.secondsToggle.checked;
      state.includeSeconds = checked;
      const tokens = DOM.cronInput.value.trim().split(/\s+/).filter(Boolean);

      if (checked && tokens.length === 5) {
        DOM.cronInput.value = `0 ${DOM.cronInput.value.trim()}`;
      } else if (!checked && tokens.length === 6) {
        DOM.cronInput.value = tokens.slice(1).join(' ');
      }
      handleInputChange();
    });

    if (DOM.copyExplanationBtn) {
      DOM.copyExplanationBtn.addEventListener('click', () => {
        const text = DOM.humanReadable.textContent;
        navigator.clipboard.writeText(text).then(() => {
          showToast('Copied human-readable description!');
        });
      });
    }

    if (DOM.copyCronBtn) {
      DOM.copyCronBtn.addEventListener('click', () => {
        navigator.clipboard.writeText(DOM.cronInput.value.trim()).then(() => {
          showToast('Copied cron expression!');
        });
      });
    }

    if (DOM.shareBtn) {
      DOM.shareBtn.addEventListener('click', () => {
        const url = window.location.href;
        navigator.clipboard.writeText(url).then(() => {
          showToast('Shareable URL copied to clipboard!');
        });
      });
    }

    if (DOM.datesCountSelect) {
      DOM.datesCountSelect.addEventListener('change', () => {
        if (state.lastValidParsed) {
          renderUpcomingDates(state.lastValidParsed);
        }
      });
    }

    if (DOM.tzLocalBtn && DOM.tzUtcBtn) {
      DOM.tzLocalBtn.addEventListener('click', () => {
        state.timezoneMode = 'local';
        DOM.tzLocalBtn.classList.add('active');
        DOM.tzUtcBtn.classList.remove('active');
        if (state.lastValidParsed) renderUpcomingDates(state.lastValidParsed);
      });

      DOM.tzUtcBtn.addEventListener('click', () => {
        state.timezoneMode = 'utc';
        DOM.tzUtcBtn.classList.add('active');
        DOM.tzLocalBtn.classList.remove('active');
        if (state.lastValidParsed) renderUpcomingDates(state.lastValidParsed);
      });
    }

    if (DOM.copyDatesBtn) {
      DOM.copyDatesBtn.addEventListener('click', () => {
        if (!state.lastValidParsed) return;
        const count = parseInt(DOM.datesCountSelect ? DOM.datesCountSelect.value : 10, 10) || 10;
        const dates = getNextRunDates(state.lastValidParsed, count, new Date());
        const lines = dates.map((d, i) => `${i + 1}. ${d.toISOString()} (${state.timezoneMode === 'utc' ? d.toUTCString() : d.toLocaleString()})`);
        navigator.clipboard.writeText(lines.join('\n')).then(() => {
          showToast(`Copied ${dates.length} scheduled dates!`);
        });
      });
    }

    setupQuickBuilder();
  }

  function setupQuickBuilder() {
    const builderFreq = document.getElementById('builderFreq');
    const builderTime = document.getElementById('builderTime');
    const applyBuilderBtn = document.getElementById('applyBuilderBtn');

    if (!builderFreq || !applyBuilderBtn) return;

    applyBuilderBtn.addEventListener('click', () => {
      const freq = builderFreq.value;
      let newExpr = '* * * * *';
      let sec = false;

      if (freq === 'minute') {
        newExpr = '* * * * *';
      } else if (freq === '5min') {
        newExpr = '*/5 * * * *';
      } else if (freq === '15min') {
        newExpr = '*/15 * * * *';
      } else if (freq === 'hourly') {
        newExpr = '0 * * * *';
      } else if (freq === 'daily') {
        const timeVal = builderTime ? builderTime.value : '09:00';
        const [hh, mm] = timeVal.split(':');
        newExpr = `${parseInt(mm, 10) || 0} ${parseInt(hh, 10) || 0} * * *`;
      } else if (freq === 'weekdays') {
        const timeVal = builderTime ? builderTime.value : '09:00';
        const [hh, mm] = timeVal.split(':');
        newExpr = `${parseInt(mm, 10) || 0} ${parseInt(hh, 10) || 0} * * 1-5`;
      } else if (freq === 'monthly') {
        newExpr = '0 0 1 * *';
      }

      state.includeSeconds = sec;
      DOM.secondsToggle.checked = sec;
      DOM.cronInput.value = newExpr;
      handleInputChange();
      showToast('Applied schedule from builder!');
    });
  }

  function init() {
    cacheDOMElements();
    renderPresets();
    setupEventListeners();

    const loaded = loadFromUrlHash();
    if (!loaded) {
      DOM.cronInput.value = '* * * * *';
      DOM.secondsToggle.checked = false;
    }
    handleInputChange();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
